import { DeleteOutlined, KeyOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  createAdmin,
  deleteUser,
  getUsers,
  updateUserPassword,
} from "../../api/users.api";
import Card from "../../components/ui/Card";
import Spinner from "../../components/ui/Spinner";
import Table from "../../components/ui/Table";
import { useAuth } from "../../hooks/useAuth";
import { formatDate } from "../../utils/formatters";

interface AdminUser {
  id: string;
  username: string;
  email: string;
  is_active: boolean;
  created_at: string;
}

const UserManagementPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);

  // Edit password modal
  const [pwdTarget, setPwdTarget] = useState<AdminUser | null>(null);
  const [pwdForm] = Form.useForm();
  const [updatingPwd, setUpdatingPwd] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await getUsers({ role: "admin" });
      setUsers(data.users ?? []);
    } catch {
      setError("Unable to load admin users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onCreateSubmit = async (values: {
    username: string;
    email: string;
    password: string;
  }) => {
    try {
      setCreating(true);
      await createAdmin(values);
      toast.success("Admin created");
      setCreateOpen(false);
      createForm.resetFields();
      await load();
    } catch {
      toast.error("Unable to create admin");
    } finally {
      setCreating(false);
    }
  };

  const onPasswordSubmit = async (values: { password: string }) => {
    if (!pwdTarget) return;
    try {
      setUpdatingPwd(true);
      await updateUserPassword(pwdTarget.id, values.password);
      toast.success("Password updated");
      setPwdTarget(null);
      pwdForm.resetFields();
    } catch {
      toast.error("Unable to update password");
    } finally {
      setUpdatingPwd(false);
    }
  };

  const onDelete = async (userId: string) => {
    try {
      await deleteUser(userId);
      toast.success("Admin deleted");
      await load();
    } catch {
      toast.error("Unable to delete admin");
    }
  };

  return (
    <div className="page-stack">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography.Title level={2} style={{ margin: 0 }}>
          User Management
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
        >
          Add Admin
        </Button>
      </div>

      {error && <Alert type="error" showIcon message={error} />}
      {loading ? (
        <Spinner />
      ) : (
        <Card>
          <Table
            dataSource={users}
            rowKey="id"
            columns={[
              { title: "Username", dataIndex: "username" },
              { title: "Email", dataIndex: "email" },
              {
                title: "Created",
                dataIndex: "created_at",
                render: (v: string) => formatDate(v),
              },
              {
                title: "Actions",
                render: (_: unknown, record: AdminUser) => (
                  <Space>
                    <Button
                      size="small"
                      icon={<KeyOutlined />}
                      onClick={() => {
                        setPwdTarget(record);
                        pwdForm.resetFields();
                      }}
                    >
                      Password
                    </Button>
                    <Popconfirm
                      title="Delete this admin?"
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      disabled={record.id === currentUser?.id}
                      onConfirm={() => onDelete(record.id)}
                    >
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={record.id === currentUser?.id}
                      >
                        Delete
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      )}

      {/* Create admin modal */}
      <Modal
        title="Add New Admin"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          createForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={onCreateSubmit}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, min: 3 }]}
          >
            <Input placeholder="admin_user" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: "email" }]}
          >
            <Input placeholder="admin@example.com" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, min: 8 }]}
          >
            <Input.Password placeholder="Min 8 characters" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                Create
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Change password modal */}
      <Modal
        title={`Change Password — ${pwdTarget?.username ?? ""}`}
        open={!!pwdTarget}
        onCancel={() => {
          setPwdTarget(null);
          pwdForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={pwdForm}
          layout="vertical"
          onFinish={onPasswordSubmit}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="password"
            label="New Password"
            rules={[{ required: true, min: 8 }]}
          >
            <Input.Password placeholder="Min 8 characters" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setPwdTarget(null)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={updatingPwd}>
                Update
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagementPage;
